import { GlowLineHorizontal, GlowLineVertical, PathNode } from '@/components/shared/PathDiagram'

export type BaSkillPathNodeId =
  | 'check-running'
  | 'asis'
  | 'kg'
  | 'elicitation'
  | 'gap'
  | 'data-model'
  | 'state-diagram'
  | 'prd'
  | 'ui-design'
  | 'sequence'
  | 'activity-rule'
  | 'us-spec'
  | 'cr'

interface BaSkillPathDiagramProps {
  activeStepId?: string | null
  onSelectStep?: (stepId: BaSkillPathNodeId) => void
  className?: string
}

const BranchLabel = ({ children }: { children: string }) => (
  <span className="relative z-30 text-[10px] sm:text-xs font-label font-semibold uppercase tracking-wide text-[#a67c2a] shrink-0 bg-m2-surface-container/90 px-1 rounded-sm">
    {children}
  </span>
)

/** Cân độ rộng/cao node trong grid — tránh node dài ngắn lệch */
const nodeCls =
  'w-full min-h-[3.25rem] flex items-center justify-center px-2 py-2 text-[11px] sm:text-[12px] leading-snug'

const BaSkillPathDiagram = ({
  activeStepId = null,
  onSelectStep,
  className = '',
}: BaSkillPathDiagramProps) => {
  const select = (id: BaSkillPathNodeId) => () => onSelectStep?.(id)
  const isActive = (id: BaSkillPathNodeId) => activeStepId === id

  return (
    <div
      className={`ba-skill-path-diagram m2-glass-card relative w-full rounded-xl border border-m2-primary/25 px-3 sm:px-5 py-5 sm:py-6 overflow-hidden ${className}`}
      role="img"
      aria-label="Workflow E2E — Nối task thành hệ thống AI bạn điều phối"
    >
      <div className="absolute inset-0 m2-cta-gradient-br opacity-[0.07] pointer-events-none" />

      <div className="relative text-center mb-4 sm:mb-5">
        <p className="text-sm sm:text-base font-bold text-m2-on-surface">Workflow E2E</p>
        <p className="mt-1 text-xs sm:text-sm text-m2-on-surface-variant leading-relaxed max-w-xl mx-auto">
          Nối task thành hệ thống AI bạn điều phối
        </p>
      </div>

      {/* ===== Mobile ===== */}
      <div className="relative flex flex-col items-stretch gap-2 lg:hidden">
        <div className="flex justify-center">
          <PathNode
            variant="decision"
            active={isActive('check-running')}
            onClick={select('check-running')}
          >
            Dự án đã chạy?
          </PathNode>
        </div>
        <div className="flex items-center justify-center gap-2">
          <BranchLabel>Đúng</BranchLabel>
          <GlowLineVertical delay="0s" />
        </div>
        <PathNode variant="default" active={isActive('asis')} onClick={select('asis')} className={nodeCls}>
          Tổng hợp hiện trạng HT
        </PathNode>
        <GlowLineVertical delay="0.15s" className="mx-auto" />
        <PathNode variant="default" active={isActive('kg')} onClick={select('kg')} className={nodeCls}>
          Tạo Knowledge Graph
        </PathNode>
        <div className="flex items-center justify-center gap-2">
          <BranchLabel>→ Khơi gợi</BranchLabel>
          <GlowLineVertical delay="0.25s" />
        </div>
        <p className="text-center text-[10px] text-m2-on-surface-variant -mt-1">(Sai → vào thẳng Khơi gợi)</p>
        <PathNode variant="default" active={isActive('elicitation')} onClick={select('elicitation')} className={nodeCls}>
          Khơi gợi yêu cầu
        </PathNode>
        <GlowLineVertical delay="0.35s" className="mx-auto" />
        <PathNode variant="default" active={isActive('gap')} onClick={select('gap')} className={nodeCls}>
          Gap Analysis
        </PathNode>
        <GlowLineVertical delay="0.4s" className="mx-auto" />
        <PathNode variant="default" active={isActive('data-model')} onClick={select('data-model')} className={nodeCls}>
          Data model
        </PathNode>
        <GlowLineVertical delay="0.45s" className="mx-auto" />
        <PathNode
          variant="default"
          active={isActive('state-diagram')}
          onClick={select('state-diagram')}
          className={nodeCls}
        >
          State diagram
        </PathNode>
        <GlowLineVertical delay="0.5s" className="mx-auto" />
        <PathNode variant="default" active={isActive('prd')} onClick={select('prd')} className={nodeCls}>
          Tạo PRD
        </PathNode>
        <GlowLineVertical delay="0.55s" className="mx-auto" />
        <PathNode variant="default" active={isActive('ui-design')} onClick={select('ui-design')} className={nodeCls}>
          Thiết kế màn hình
        </PathNode>
        <GlowLineVertical delay="0.6s" className="mx-auto" />
        <PathNode variant="default" active={isActive('sequence')} onClick={select('sequence')} className={nodeCls}>
          Sequence diagram
        </PathNode>
        <GlowLineVertical delay="0.65s" className="mx-auto" />
        <PathNode
          variant="default"
          active={isActive('activity-rule')}
          onClick={select('activity-rule')}
          className={nodeCls}
        >
          Activity diagram/Rule validate
        </PathNode>
        <GlowLineVertical delay="0.7s" className="mx-auto" />
        <PathNode variant="default" active={isActive('us-spec')} onClick={select('us-spec')} className={nodeCls}>
          Đặc tả US theo loại
        </PathNode>
        <GlowLineVertical delay="0.75s" accent className="mx-auto" />
        <PathNode variant="default" active={isActive('cr')} onClick={select('cr')} className={nodeCls}>
          Quản lý CR
        </PathNode>
        <div className="flex flex-col items-center gap-1 mt-1">
          <BranchLabel>Thay đổi</BranchLabel>
          <GlowLineVertical delay="0.8s" accent reverse className="!h-8" />
          <button
            type="button"
            onClick={select('elicitation')}
            className="text-[11px] font-semibold text-[#a67c2a] underline-offset-2 hover:underline"
          >
            ↑ về Khơi gợi yêu cầu
          </button>
        </div>
      </div>

      {/* ===== Desktop: 5 cột node đều + cột connector hẹp ===== */}
      <div className="relative hidden lg:grid lg:grid-cols-[minmax(0,1fr)_3.25rem_minmax(0,1fr)_2.25rem_minmax(0,1fr)_2.25rem_minmax(0,1fr)_2.25rem_minmax(0,1fr)] lg:gap-x-2.5 lg:gap-y-3 lg:items-center">
        {/* Row 1: Decision —Đúng→ Hiện trạng ———→ KG */}
        <div className="relative z-20 flex justify-center px-1">
          <PathNode
            variant="decision"
            active={isActive('check-running')}
            onClick={select('check-running')}
            className="ba-skill-decision"
          >
            Dự án đã chạy?
          </PathNode>
        </div>
        <div className="relative z-30 flex flex-col items-center justify-center gap-1 min-w-0">
          <BranchLabel>Đúng</BranchLabel>
          <GlowLineHorizontal delay="0s" className="!w-8" />
        </div>
        <PathNode
          variant="default"
          delay="0.15s"
          active={isActive('asis')}
          onClick={select('asis')}
          className={nodeCls}
        >
          Tổng hợp hiện trạng HT
        </PathNode>
        <div className="col-span-5 flex items-center min-w-0 px-1">
          <GlowLineHorizontal delay="0.25s" className="flex-1 !w-auto" />
        </div>
        <PathNode
          variant="default"
          delay="0.35s"
          active={isActive('kg')}
          onClick={select('kg')}
          className={nodeCls}
        >
          Tạo Knowledge Graph
        </PathNode>

        {/* Row 1b: Sai ↓ (label cạnh đường) | KG stub ⊤ → Khơi gợi */}
        <div className="relative z-30 flex items-center justify-center gap-1.5 py-1">
          <BranchLabel>Sai</BranchLabel>
          <GlowLineVertical delay="0.1s" className="!h-10" />
        </div>
        <div className="col-span-8 flex items-end gap-2 min-w-0 px-1 pb-1">
          <div className="flex-1 flex items-center min-w-0 gap-2 self-end">
            <GlowLineHorizontal delay="0.4s" reverse className="flex-1 !w-auto" />
            <span className="text-[10px] font-label text-[#a67c2a] whitespace-nowrap shrink-0">
              KG → Khơi gợi
            </span>
            <GlowLineHorizontal delay="0.5s" reverse arrow={false} className="flex-1 !w-auto" />
          </div>
          <GlowLineVertical delay="0.45s" arrow={false} className="!h-8 shrink-0" />
        </div>

        {/* Row 2: Khơi gợi → Gap → Data model → State diagram → PRD */}
        <PathNode
          variant="default"
          delay="0.55s"
          active={isActive('elicitation')}
          onClick={select('elicitation')}
          className={nodeCls}
        >
          Khơi gợi yêu cầu
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="0.6s" className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.65s"
          active={isActive('gap')}
          onClick={select('gap')}
          className={nodeCls}
        >
          Gap Analysis
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="0.7s" className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.75s"
          active={isActive('data-model')}
          onClick={select('data-model')}
          className={nodeCls}
        >
          Data model
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="0.8s" className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.82s"
          active={isActive('state-diagram')}
          onClick={select('state-diagram')}
          className={nodeCls}
        >
          State diagram
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="0.84s" className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.85s"
          active={isActive('prd')}
          onClick={select('prd')}
          className={nodeCls}
        >
          Tạo PRD
        </PathNode>

        {/* Row 2b: CR ↑ Elicit | PRD ↓ MH */}
        <div className="flex flex-col items-center gap-1">
          <GlowLineVertical delay="1.3s" accent reverse className="!h-9" />
          <BranchLabel>Thay đổi</BranchLabel>
        </div>
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
        <div className="flex justify-center">
          <GlowLineVertical delay="0.9s" className="!h-9" />
        </div>

        {/* Row 3: CR ← US ← Activity ← Sequence ← Thiết kế MH */}
        <PathNode
          variant="default"
          delay="1.25s"
          active={isActive('cr')}
          onClick={select('cr')}
          className={nodeCls}
        >
          Quản lý CR
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="1.2s" accent reverse className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="1.15s"
          active={isActive('us-spec')}
          onClick={select('us-spec')}
          className={nodeCls}
        >
          Đặc tả US theo loại
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="1.1s" reverse className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="1.05s"
          active={isActive('activity-rule')}
          onClick={select('activity-rule')}
          className={nodeCls}
        >
          Activity diagram / Rule validate
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="1s" reverse className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.95s"
          active={isActive('sequence')}
          onClick={select('sequence')}
          className={nodeCls}
        >
          Sequence diagram
        </PathNode>
        <div className="flex justify-center">
          <GlowLineHorizontal delay="0.92s" reverse className="!w-7" />
        </div>
        <PathNode
          variant="default"
          delay="0.9s"
          active={isActive('ui-design')}
          onClick={select('ui-design')}
          className={nodeCls}
        >
          Thiết kế màn hình
        </PathNode>
      </div>
    </div>
  )
}

export default BaSkillPathDiagram
